/**
 * Every dispatch of one kind of call.
 *
 * The feed behind a row of the call index: who called it, with what arguments,
 * in which block, and whether it worked. Nothing here is written per call type
 * — the arguments render from the runtime's own names, so a pallet added next
 * year has a working page the moment somebody uses it.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { BlockExtrinsic } from '../api/generated/BlockExtrinsic'
import { RequestFailed, fetchCallFeed } from '../api/rest'
import { height as fmtHeight, shortAddress, when } from '../lib/format'
import { href } from '../lib/routes'
import { Payload } from './Payload'

export function CallPanel({
  chain,
  pallet,
  call,
  decimals,
  symbol,
}: {
  chain: string
  pallet: string
  call: string
  decimals: number
  symbol: string
}) {
  const [rows, setRows] = useState<BlockExtrinsic[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [before, setBefore] = useState<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setRows(null)
    setError(null)
    fetchCallFeed(chain, pallet, call, before ?? undefined, controller.signal)
      .then(setRows)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain, pallet, call, before])

  const oldest = rows && rows.length > 0 ? rows[rows.length - 1]!.height : null

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <div>
          <div className="eyebrow">Call</div>
          <h2 className="panel-title" style={{ marginTop: 4, textTransform: 'none' }}>
            <code>
              {pallet}::{call}
            </code>
          </h2>
        </div>
        <Link className="segmented panel-button" to={href({ chain, index: 'call' })}>
          All calls
        </Link>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && rows === null && <p className="empty">Reading the record…</p>}
      {rows !== null && rows.length === 0 && (
        <p className="empty">
          No dispatch of this call in the indexed range. It is declared by the runtime and has not
          been used here.
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Every dispatch of {pallet}::{call}, newest first
            </caption>
            <thead>
              <tr>
                <th scope="col">Block</th>
                <th scope="col" className="left">
                  Signer
                </th>
                <th scope="col" className="left">
                  Arguments
                </th>
                <th scope="col" className="event-when">
                  When
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={`${x.height}-${x.index}`}>
                  <td className="numeral">
                    <span className="miner-cell">
                      <Link to={href({ chain, block: String(x.height) })}>
                        #{fmtHeight(x.height)}
                      </Link>
                      {x.success === false && (
                        <span className="chip chip-failed" title="The dispatch failed.">
                          failed
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="left">
                    {x.signer_address ? (
                      <Link
                        className="numeral"
                        to={href({ chain, account: x.signer_address })}
                        title={`${x.signer_address}\n${x.signer}`}
                      >
                        {shortAddress(x.signer_address)}
                      </Link>
                    ) : (
                      <span className="runtime-modifier">inherent</span>
                    )}
                  </td>
                  <td className="left">
                    <Payload
                      value={x.args}
                      ctx={{ chain, addresses: x.addresses, decimals, symbol }}
                    />
                  </td>
                  <td className="numeral event-when" style={{ color: 'var(--text-muted)' }}>
                    {when(x.at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pager">
        <button
          className="segmented panel-button"
          disabled={before === null}
          onClick={() => setBefore(null)}
        >
          ← Newest
        </button>
        <button
          className="segmented panel-button"
          disabled={oldest === null || oldest <= 1}
          onClick={() => setBefore(oldest)}
        >
          Older →
        </button>
      </div>
    </section>
  )
}
